/**
 * Minimal EIP-1193 provider that talks to the UZH Wallet web app the same way
 * other UZH teaching sites do (EvmLinkWallet): by payment / connect links.
 *
 * Exposed through the `uzhLink` connector, next to MetaMask and the other wallets.
 * Does not modify the wallet — only opens https://bdlt-services.ifi.uzh.ch/wallet/
 * with #connect= / #pay= / #return= and reads #uzhwallet= / #uzhtx= on return.
 */

const ADDR_KEY = 'uzh.linkWallet.address'
const CHAIN_KEY = 'uzh.linkWallet.chainId'
const PENDING_KEY = 'uzh.linkWallet.pending'
const WALLET_URL = 'https://bdlt-services.ifi.uzh.ch/wallet/'
const RPC_URL = 'https://bdlt-services.ifi.uzh.ch/rpc/eth_pos'
const DEFAULT_CHAIN_ID = 70207

type Listener = (arg?: unknown) => void

function isAddress(s: unknown): s is string {
  return typeof s === 'string' && /^0x[0-9a-fA-F]{40}$/.test(s.trim())
}

function hexChainId(id: number): string {
  return '0x' + id.toString(16)
}

function providerError(code: number, message: string): Error {
  const e = new Error(message) as Error & { code: number }
  e.code = code
  return e
}

function pageBaseUrl(): string {
  // HashRouter: wallet return URL must not include #/route (wallet replies with #uzhwallet=).
  return window.location.href.split('#')[0]
}

function readStoredAddress(): string | null {
  try {
    const a = localStorage.getItem(ADDR_KEY)
    return isAddress(a) ? a.toLowerCase() : null
  } catch {
    return null
  }
}

function writeStoredAddress(a: string | null) {
  try {
    if (a) localStorage.setItem(ADDR_KEY, a)
    else localStorage.removeItem(ADDR_KEY)
  } catch {
    /* private mode */
  }
}

function readStoredChainId(): number {
  try {
    const c = Number(localStorage.getItem(CHAIN_KEY))
    return c > 0 ? c : DEFAULT_CHAIN_ID
  } catch {
    return DEFAULT_CHAIN_ID
  }
}

function writeStoredChainId(id: number) {
  try {
    localStorage.setItem(CHAIN_KEY, String(id))
  } catch {
    /* ignore */
  }
}

/** Consume #uzhwallet= / #uzhtx= from the fragment (same contract as EvmLinkWallet). */
function takeWalletReturn(): { address?: string; txHash?: string } {
  const raw = window.location.hash.replace(/^#/, '')
  if (!raw) return {}
  const parts = raw.split('&').filter(Boolean)
  const rest: string[] = []
  const got: Record<string, string> = {}
  for (const part of parts) {
    const m = /^(uzhwallet|uzhtx)=(.*)$/.exec(part)
    if (!m) {
      rest.push(part)
      continue
    }
    try {
      got[m[1]] = decodeURIComponent(m[2])
    } catch {
      got[m[1]] = m[2]
    }
  }
  if (got.uzhwallet == null && got.uzhtx == null) return {}

  const nextHash = rest.length ? '#' + rest.join('&') : ''
  try {
    history.replaceState(null, '', window.location.pathname + window.location.search + nextHash)
  } catch {
    /* ignore */
  }

  const out: { address?: string; txHash?: string } = {}
  if (isAddress(got.uzhwallet)) out.address = got.uzhwallet.toLowerCase()
  if (/^(0x)?[0-9a-fA-F]{64}$/.test(got.uzhtx || '')) {
    out.txHash = got.uzhtx.startsWith('0x') ? got.uzhtx : '0x' + got.uzhtx
  }
  return out
}

async function rpc(method: string, params: unknown[] = []): Promise<unknown> {
  const r = await fetch(RPC_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', id: Date.now(), method, params }),
  })
  if (!r.ok) throw providerError(-32603, `RPC HTTP ${r.status}`)
  const j = await r.json()
  if (j.error) throw providerError(j.error.code || -32603, j.error.message || 'RPC error')
  return j.result
}

function buildPayLink(tx: { to?: string; data?: string; input?: string; value?: string }): string {
  if (!isAddress(tx.to)) {
    throw providerError(4200, 'Missing transaction recipient.')
  }
  const to = tx.to.toLowerCase()
  const data = (tx.data || tx.input || '0x').toLowerCase()
  const value = tx.value != null && tx.value !== '' && tx.value !== '0x' ? BigInt(tx.value).toString(10) : '0'
  if (!data || data === '0x') {
    return `ethereum:${to}@${DEFAULT_CHAIN_ID}?value=${value}`
  }
  // Wallet 0.10+ accepts raw calldata via ?data= (requests includes "data").
  return `ethereum:${to}@${DEFAULT_CHAIN_ID}?data=${data}${value !== '0' ? `&value=${value}` : ''}`
}

export type UzhLinkEthereumProvider = {
  isUzhLinkWallet: true
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>
  on: (event: string, fn: Listener) => UzhLinkEthereumProvider
  removeListener: (event: string, fn: Listener) => UzhLinkEthereumProvider
  enable: () => Promise<unknown>
  send: (methodOrPayload: string | { method: string; params?: unknown[] }, paramsOrCallback?: unknown) => unknown
  setAddress: (address: string) => void
}

export function createUzhLinkWalletProvider(): UzhLinkEthereumProvider {
  const returned = takeWalletReturn()
  let address: string | null = returned.address || readStoredAddress()
  let chainId = readStoredChainId()
  const handlers: Record<string, Listener[]> = {}

  if (returned.address) {
    writeStoredAddress(returned.address)
    try {
      localStorage.removeItem('uzh.evmWallet.disconnected')
    } catch {
      /* ignore */
    }
  }

  // If we returned from a payment with a tx hash, remember it for the reloaded app.
  if (returned.txHash) {
    try {
      sessionStorage.setItem('uzh.linkWallet.returnedTx', returned.txHash)
      const pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null')
      if (pending) {
        pending.returnedTx = returned.txHash
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending))
      }
    } catch {
      /* ignore */
    }
  }

  const emit = (event: string, arg?: unknown) => {
    ;(handlers[event] || []).slice().forEach((fn) => {
      try {
        fn(arg)
      } catch {
        /* listener fault */
      }
    })
  }

  const setAddress = (a: string | null) => {
    const changed = a !== address
    address = a
    writeStoredAddress(a)
    if (changed) emit('accountsChanged', a ? [a] : [])
  }

  const setChain = (id: number) => {
    const changed = id !== chainId
    chainId = id
    writeStoredChainId(id)
    if (changed) emit('chainChanged', hexChainId(id))
  }

  const connectViaWallet = (): Promise<string> => {
    // Navigate to wallet; when user shares address, this page reloads with #uzhwallet=.
    const connectUrl = `${WALLET_URL}#connect=${encodeURIComponent(pageBaseUrl())}`
    window.location.assign(connectUrl)
    // Page unloads; promise unused after navigation.
    return new Promise(() => undefined)
  }

  const sendViaWallet = async (tx: Record<string, string>): Promise<string> => {
    if (!address) throw providerError(4100, 'Connect first.')
    const link = buildPayLink(tx)
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify({ t: Date.now(), from: address, link, page: pageBaseUrl() }))
    } catch {
      /* ignore */
    }
    window.location.assign(`${WALLET_URL}#return=${encodeURIComponent(pageBaseUrl())}&pay=${encodeURIComponent(link)}`)
    return new Promise(() => undefined)
  }

  // After return from pay: if we stored returnedTx, surface it once for dapp polling.
  const consumeReturnedTx = (): string | null => {
    try {
      const direct = sessionStorage.getItem('uzh.linkWallet.returnedTx')
      if (direct) {
        sessionStorage.removeItem('uzh.linkWallet.returnedTx')
        sessionStorage.removeItem(PENDING_KEY)
        return direct
      }
      const pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null')
      if (pending?.returnedTx) {
        const hash = pending.returnedTx as string
        sessionStorage.removeItem(PENDING_KEY)
        return hash
      }
    } catch {
      /* ignore */
    }
    return null
  }

  const provider: UzhLinkEthereumProvider = {
    isUzhLinkWallet: true,
    async request({ method, params = [] }) {
      switch (method) {
        case 'eth_accounts':
          return address ? [address] : []
        case 'eth_requestAccounts': {
          if (address) return [address]
          await connectViaWallet()
          return address ? [address] : []
        }
        case 'wallet_requestPermissions': {
          if (!address) await connectViaWallet()
          return [{ parentCapability: 'eth_accounts' }]
        }
        case 'wallet_revokePermissions':
          setAddress(null)
          return null
        case 'wallet_getPermissions':
          return address ? [{ parentCapability: 'eth_accounts' }] : []
        case 'eth_chainId':
          return hexChainId(chainId)
        case 'net_version':
          return String(chainId)
        case 'wallet_switchEthereumChain':
        case 'wallet_addEthereumChain': {
          const want = Number(BigInt((params[0] as { chainId?: string })?.chainId || hexChainId(chainId)))
          if (want !== DEFAULT_CHAIN_ID) {
            throw providerError(4902, `UZH Wallet link mode only supports chain ${DEFAULT_CHAIN_ID}.`)
          }
          setChain(want)
          return null
        }
        case 'wallet_watchAsset':
          return true
        case 'eth_sendTransaction': {
          const cached = consumeReturnedTx()
          if (cached) return cached
          return sendViaWallet((params[0] || {}) as Record<string, string>)
        }
        case 'personal_sign':
        case 'eth_signTypedData':
        case 'eth_signTypedData_v3':
        case 'eth_signTypedData_v4':
        case 'eth_sign':
          throw providerError(
            4200,
            'Message signing via UZH Wallet link is not wired in UZHSwap yet. Use a browser extension on desktop if needed.'
          )
        default:
          // Reads / misc: forward to HTTPS RPC relay (same as other UZH sites).
          return rpc(method, params as unknown[])
      }
    },
    on(event, fn) {
      ;(handlers[event] = handlers[event] || []).push(fn)
      return provider
    },
    removeListener(event, fn) {
      handlers[event] = (handlers[event] || []).filter((f) => f !== fn)
      return provider
    },
    // web3-react v6 InjectedConnector uses the legacy send()/enable() API.
    enable() {
      return provider.request({ method: 'eth_requestAccounts' })
    },
    send(methodOrPayload: string | { method: string; params?: unknown[] }, paramsOrCallback?: unknown) {
      if (typeof methodOrPayload === 'string') {
        return provider.request({ method: methodOrPayload, params: (paramsOrCallback as unknown[]) || [] })
      }
      if (typeof paramsOrCallback === 'function') {
        const callback = paramsOrCallback as (error: unknown, response?: unknown) => void
        provider
          .request(methodOrPayload)
          .then((result) => callback(null, { id: 1, jsonrpc: '2.0', result }))
          .catch((error) => callback(error))
        return undefined
      }
      throw providerError(4200, 'Synchronous send is not supported.')
    },
    // Address pasted by hand (as on the wrapper pages) when the #connect= hand-over cannot reach the wallet.
    setAddress(a: string) {
      if (!isAddress(a)) throw providerError(4001, 'An address is 0x followed by 40 characters.')
      setAddress(a.trim().toLowerCase())
    },
  }

  return provider
}

let sharedProvider: UzhLinkEthereumProvider | undefined

/** Must run before the router reads the hash, so #uzhwallet= / #uzhtx= are consumed first. */
export function getUzhLinkWalletProvider(): UzhLinkEthereumProvider {
  return (sharedProvider = sharedProvider ?? createUzhLinkWalletProvider())
}

export const UZH_WALLET_URL = WALLET_URL

export function isEvmAddress(s: unknown): boolean {
  return isAddress(s)
}

export function hasUzhLinkAddress(): boolean {
  return readStoredAddress() !== null
}

/** Another wallet was chosen: stop reconnecting UZH Wallet on reload. */
export function forgetUzhLinkAddress(): void {
  if (hasUzhLinkAddress()) getUzhLinkWalletProvider().request({ method: 'wallet_revokePermissions' })
}

export function uzhWalletHint(): string {
  const standalone =
    !!(navigator as Navigator & { standalone?: boolean }).standalone ||
    window.matchMedia?.('(display-mode: standalone)').matches
  // A Home Screen web app and Safari keep separate storage: Safari only reaches the wallet stored in Safari.
  if (/iPhone|iPad|iPod/.test(navigator.userAgent) && !standalone) {
    return 'Wallet on the Home Screen? Open it and choose Menu → Sites to use UZHSwap from inside it.'
  }
  return 'Opens the UZH Wallet to share your address'
}

export function peekReturnedTxHash(): string | null {
  try {
    return sessionStorage.getItem('uzh.linkWallet.returnedTx')
  } catch {
    return null
  }
}

export function clearReturnedTxHash(): void {
  try {
    sessionStorage.removeItem('uzh.linkWallet.returnedTx')
    sessionStorage.removeItem(PENDING_KEY)
  } catch {
    /* ignore */
  }
}
